import { useState } from "react";
import { AlertTriangle, Check, Copy, Eye, EyeOff } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * PasswordRevealDialog — muestra un password PPPoE UNA SOLA VEZ.
 *
 * Diseñado para:
 *   1. Después de crear un contrato (response incluye pppoe_password_plain)
 *   2. Después de regenerar password de un contrato existente
 *
 * UX:
 *   - Warning grande: "Solo se muestra una vez. Guárdala."
 *   - Password oculto por default, toggle para mostrar (en caso de pantalla compartida)
 *   - Botón "Copiar" con feedback visual
 *   - Botón principal "Ya la guardé" que confirma y cierra
 *   - No se puede cerrar clickeando fuera (prevent accidental close)
 */
export function PasswordRevealDialog({
  open,
  onConfirm,
  pppoeUser,
  password,
  title = "Password PPPoE generado",
  subtitle,
}: {
  open: boolean;
  onConfirm: () => void;
  pppoeUser?: string;
  password: string;
  title?: string;
  subtitle?: string;
}) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select + prompt
      alert(`Copia manualmente:\n\n${password}`);
    }
  }

  return (
    <Dialog open={open} onOpenChange={() => undefined} closeOnBackdrop={false}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-500" />
          {title}
        </DialogTitle>
        <DialogDescription>
          {subtitle ??
            "Esta password solo se muestra una vez. Cópiala y guárdala en un lugar seguro antes de cerrar. No se podrá recuperar después."}
        </DialogDescription>
      </DialogHeader>

      <DialogContent>
        {pppoeUser && (
          <div className="mb-3">
            <div className="text-[10px] font-semibold uppercase text-muted-foreground">
              Usuario PPPoE
            </div>
            <div className="font-mono text-sm">{pppoeUser}</div>
          </div>
        )}

        <div>
          <div className="text-[10px] font-semibold uppercase text-muted-foreground">
            Password PPPoE
          </div>
          <div className="mt-1 flex items-center gap-2">
            <Input
              readOnly
              type={visible ? "text" : "password"}
              value={password}
              className="font-mono text-sm"
              onFocus={(e) => e.target.select()}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setVisible((v) => !v)}
              title={visible ? "Ocultar" : "Mostrar"}
            >
              {visible ? <EyeOff size={14} /> : <Eye size={14} />}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copiado" : "Copiar"}
            </Button>
          </div>
        </div>

        <div className="mt-4 rounded-md border border-amber-300/50 bg-amber-50/70 dark:bg-amber-950/20 p-3 text-xs text-amber-800 dark:text-amber-300">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <strong>Importante:</strong> esta password la necesitará el técnico para
              configurar el CPE/ONU del cliente. Si se pierde, tendrás que regenerarla
              (lo que requiere re-programar el CPE con la nueva).
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogFooter>
        <Button size="sm" onClick={onConfirm}>
          Ya la guardé
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
